import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { chromeControlClass } from "../lib/chrome-control.js";
import { cn } from "../lib/utils.js";

/*
 * Sub-primitives for toolbar pills such as `FloatingToolbar` (PB-355),
 * graduated from the pbdesign oracle's `CanvasToolbar`. Both buttons
 * `forwardRef` and spread their rest props so they can be the child of a DS
 * `PopoverTrigger`/`Tooltip`:
 * the trigger clones a `ref` + aria/onClick onto its child, so a plain function
 * component would drop the ref and leave the popover unpositioned.
 *
 * Disabled buttons carry `disabled:pointer-events-none` so a wrapping `Tooltip`
 * span becomes the hover hit-target — the disabled-reason tooltip then shows
 * cross-browser (Firefox suppresses pointer events over disabled controls).
 *
 * Both paint from the internal chrome-control recipe's `toolbar` look
 * (lib/chrome-control.ts, floating-toolbar): the labelled 28px box, and with
 * `iconOnly` the 28px square. `active` is the recipe's current paint. This
 * file holds no paint class of its own for the buttons.
 */

/**
 * Thin vertical rule separating toolbar groups. Decorative (`aria-hidden`); for
 * new code outside a canvas toolbar use `Divider`, whose vertical form is a
 * `role="separator"` that stretches to its row.
 */
export function ToolbarDivider(): ReactElement {
  // DS keep (PR 3): the toolbar's fixed 20px decorative rule; Divider's
  // vertical form is a stretching role="separator".
  return <span aria-hidden className="mx-0.5 h-5 w-px bg-pb-border" />;
}

export interface ToolbarButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Leading icon (a sized lucide element). */
  readonly icon: ReactNode;
  /** Visible text label. */
  readonly label: string;
  /**
   * Active/pressed *styling* only — no aria. A disclosure (the Widget popover
   * trigger) gets `aria-expanded` from `PopoverTrigger`; a real toggle spreads
   * its own `aria-pressed` via rest props.
   */
  readonly active?: boolean;
}

/** Labelled pill action — Widget / Note / Auto-arrange / (VSM) Add lane. */
export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  function ToolbarButton(
    { icon, label, active, className, type, ...rest },
    ref,
  ) {
    return (
      // eslint-disable-next-line ds/no-hand-built -- this IS the design-system ToolbarButton: the chrome-control toolbar look, which Button has no option for
      <button
        ref={ref}
        type={type ?? "button"}
        className={cn(
          chromeControlClass({ look: "toolbar", current: active }),
          className,
        )}
        {...rest}
      >
        {icon}
        {label}
      </button>
    );
  },
);

export interface ToolbarIconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Pressed/active styling. Real toggles also pass `aria-pressed` explicitly. */
  readonly active?: boolean;
}

/** Icon-only pill action — zoom / fit / mini-map / (VSM) gear. Caller names it. */
export const ToolbarIconButton = forwardRef<
  HTMLButtonElement,
  ToolbarIconButtonProps
>(function ToolbarIconButton(
  { children, active = false, className, type, ...rest },
  ref,
) {
  return (
    // eslint-disable-next-line ds/no-hand-built -- this IS the design-system ToolbarIconButton: the chrome-control toolbar square, which Button has no option for
    <button
      ref={ref}
      type={type ?? "button"}
      className={cn(
        chromeControlClass({
          look: "toolbar",
          iconOnly: true,
          current: active,
        }),
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
